import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button, Input } from "../atoms";
import { useLogin } from "../../api/hooks/useAuth";
import { apiErrorMessage } from "../../api/client";

/** 로그인 실패를 사람이 읽을 한 줄로. 401=자격 불일치, 429=시도 제한(서버 문장), 응답 없음=연결 실패. */
export function loginError(e: unknown): string {
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (status === 401) return "아이디 또는 비밀번호가 맞지 않습니다";
  if (status === 429) return apiErrorMessage(e, "시도가 너무 많습니다. 5분 뒤 다시 시도하세요");
  if (status === undefined || status === 502 || status === 503 || status === 504) return "서버에 연결하지 못했습니다";
  return apiErrorMessage(e, "로그인하지 못했습니다");
}

/** 운영자 로그인 폼 유기체. 성공 시 대시보드로. */
export default function LoginForm() {
  const [lgnId, setLgnId] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const login = useLogin();
  const navigate = useNavigate();
  const ready = lgnId.trim() !== "" && password !== "" && !login.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    login.mutate({ lgn_id: lgnId.trim(), password }, { onSuccess: () => navigate("/", { replace: true }) });
  };
  // 고치기 시작하면 지난 오류는 걷는다.
  const edit = (set: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    set(e.target.value);
    if (login.isError) login.reset();
  };
  const caps = (e: React.KeyboardEvent<HTMLInputElement>) => setCapsLock(e.getModifierState?.("CapsLock") ?? false);

  return (
    <form className="login-form" onSubmit={onSubmit} noValidate>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">아이디</span>
        <Input value={lgnId} onChange={edit(setLgnId)} autoComplete="username" autoFocus
               aria-invalid={login.isError || undefined} className="h-10" />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">비밀번호</span>
        <span className="relative">
          <Input type={showPw ? "text" : "password"} value={password} onChange={edit(setPassword)}
                 onKeyUp={caps} onKeyDown={caps} onBlur={() => setCapsLock(false)}
                 autoComplete="current-password" aria-invalid={login.isError || undefined} className="h-10 pr-10" />
          <button type="button" onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "비밀번호 숨기기" : "비밀번호 보기"} aria-pressed={showPw}
                  className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground">
            {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </span>
        {capsLock && <span className="text-[11px] text-amber-600">Caps Lock 켜짐</span>}
      </label>
      {login.isError && (
        <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {loginError(login.error)}
        </p>
      )}
      <Button type="submit" disabled={!ready} className="h-10">
        {login.isPending ? <><Loader2 size={16} className="animate-spin" /> 로그인 중</> : "로그인"}
      </Button>
    </form>
  );
}
